import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaUsers, FaFilter, FaSearch, FaChevronDown, FaChevronRight,
  FaSyncAlt, FaUserCheck, FaUserTimes, FaTag, FaHistory,
  FaBars, FaSignOutAlt, FaUserCircle, FaServer, FaTimes,
  FaExclamationTriangle, FaCheckCircle, FaLink, FaUnlink
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getUserRole } from '../../../../utils/roleUtils';
import { clearToken } from '../../../../utils/auth';
import { getApiEndpoint } from '../../../../config/api';
import CloudStaffSidebar from '../CloudStaffSidebar';
import './CrmFunnelView.css';

const CrmFunnelView = () => {
  const navigate = useNavigate();
  const [userName, setUserName] = useState('');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('funnel');

  // Funnel data
  const [funnelData, setFunnelData] = useState(null);
  const [stages, setStages] = useState([]);

  // Contacts list
  const [contacts, setContacts] = useState([]);
  const [contactsTotal, setContactsTotal] = useState(0);
  const [filters, setFilters] = useState({
    stageKey: '', search: '', hasAppAccess: '', source: ''
  });
  const [contactsLoading, setContactsLoading] = useState(false);
  const [expandedContact, setExpandedContact] = useState(null);
  const [contactDetail, setContactDetail] = useState(null);

  // Reconciliation
  const [reconcileStatus, setReconcileStatus] = useState(null);
  const [reconcilePreview, setReconcilePreview] = useState(null);
  const [reconcileRunning, setReconcileRunning] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    const userRole = getUserRole();
    if (userRole !== 'cloud_staff' && userRole !== 'super_admin') {
      toast.error('Access denied');
      navigate('/login');
      return;
    }
    fetchProfile();
    fetchFunnelData();
    fetchReconcileStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const fetchProfile = async () => {
    try {
      const res = await fetch(getApiEndpoint('/api/users/profile'), { headers });
      if (res.ok) {
        const data = await res.json();
        setUserName(data.first_name || data.email || '');
      }
    } catch (e) { /* non-critical */ }
  };

  const fetchFunnelData = async () => {
    setLoading(true);
    try {
      const res = await fetch(getApiEndpoint('/api/crm/funnel'), { headers });
      if (!res.ok) throw new Error('Failed to load funnel');
      const data = await res.json();
      setFunnelData(data);
      setStages(data.stages || []);
    } catch (err) {
      console.error('Funnel fetch error:', err);
      toast.error('Failed to load funnel data');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchContacts = useCallback(async () => {
    setContactsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.stageKey) params.set('stageKey', filters.stageKey);
      if (filters.search) params.set('search', filters.search);
      if (filters.hasAppAccess) params.set('hasAppAccess', filters.hasAppAccess);
      if (filters.source) params.set('source', filters.source);
      params.set('limit', '50');

      const res = await fetch(getApiEndpoint(`/api/crm/contacts?${params}`), { headers });
      if (!res.ok) throw new Error('Failed to load contacts');
      const data = await res.json();
      setContacts(data.contacts || []);
      setContactsTotal(data.total || 0);
    } catch (err) {
      console.error('Contacts fetch error:', err);
    } finally {
      setContactsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    if (activeTab === 'contacts') fetchContacts();
  }, [activeTab, fetchContacts]);

  const fetchContactDetail = async (contactId) => {
    try {
      const res = await fetch(getApiEndpoint(`/api/crm/contacts/${contactId}`), { headers });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setContactDetail(data);
    } catch (err) {
      console.error('Contact detail error:', err);
    }
  };

  const handleStageChange = async (contactId, newStageKey) => {
    try {
      const res = await fetch(getApiEndpoint(`/api/crm/contacts/${contactId}/stage`), {
        method: 'PATCH', headers,
        body: JSON.stringify({ stageKey: newStageKey })
      });
      if (!res.ok) throw new Error('Failed');
      toast.success('Stage updated');
      fetchFunnelData();
      if (expandedContact === contactId) fetchContactDetail(contactId);
      fetchContacts();
    } catch (err) {
      toast.error('Failed to update stage');
    }
  };

  // Reconciliation
  const fetchReconcileStatus = async () => {
    try {
      const res = await fetch(getApiEndpoint('/api/crm/reconcile/status'), { headers });
      if (res.ok) setReconcileStatus(await res.json());
    } catch (e) { /* non-critical */ }
  };

  const handleReconcilePreview = async () => {
    try {
      const res = await fetch(getApiEndpoint('/api/crm/reconcile/preview?limit=50'), { headers });
      if (res.ok) setReconcilePreview(await res.json());
    } catch (e) { toast.error('Preview failed'); }
  };

  const handleReconcileRun = async () => {
    setReconcileRunning(true);
    try {
      const res = await fetch(getApiEndpoint('/api/crm/reconcile/run'), {
        method: 'POST', headers, body: JSON.stringify({ limit: 50 })
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Reconciled: ${data.linked} linked, ${data.created} created, ${data.skipped} skipped`);
        setReconcilePreview(data);
        fetchReconcileStatus();
        fetchFunnelData();
      }
    } catch (e) { toast.error('Reconciliation failed'); }
    finally { setReconcileRunning(false); }
  };

  const handleLogout = () => {
    clearToken();
    navigate('/login');
  };

  const toggleContact = (id) => {
    if (expandedContact === id) {
      setExpandedContact(null);
      setContactDetail(null);
    } else {
      setExpandedContact(id);
      fetchContactDetail(id);
    }
  };

  // ── Render helpers ────────────────────────────────────────
  const renderStageBadge = (stageKey, stageName, color) => (
    <span className="cfv-stage-badge" style={{ '--stage-color': color || '#38bdf8' }}>
      {stageName || stageKey}
    </span>
  );

  const renderAccessBadge = (appUserId, appRole) => {
    if (appUserId) {
      return <span className="cfv-access-badge cfv-access-active"><FaUserCheck /> Active ({appRole})</span>;
    }
    return <span className="cfv-access-badge cfv-access-none"><FaUserTimes /> No App Access</span>;
  };

  // ── FUNNEL TAB ────────────────────────────────────────────
  const renderFunnelTab = () => {
    if (loading) return <div className="cfv-loading">Loading funnel data...</div>;
    if (!funnelData) return <div className="cfv-empty">No funnel data available</div>;

    return (
      <div className="cfv-funnel-section">
        {/* Summary cards */}
        <div className="cfv-stat-row">
          <div className="cfv-stat-card">
            <div className="cfv-stat-value">{funnelData.totals.contacts}</div>
            <div className="cfv-stat-label">Total Contacts</div>
          </div>
          <div className="cfv-stat-card cfv-stat-success">
            <div className="cfv-stat-value">{funnelData.totals.withAppAccess}</div>
            <div className="cfv-stat-label">With App Access</div>
          </div>
          <div className="cfv-stat-card cfv-stat-warning">
            <div className="cfv-stat-value">{funnelData.totals.withoutAppAccess}</div>
            <div className="cfv-stat-label">CRM Only</div>
          </div>
        </div>

        {/* Pipeline stages */}
        <div className="cfv-pipeline">
          <h3 className="cfv-section-title">Pipeline Stages</h3>
          <div className="cfv-stages-grid">
            {funnelData.summary.map((stage) => (
              <button
                key={stage.stage_key}
                className={`cfv-stage-card ${filters.stageKey === stage.stage_key ? 'active' : ''}`}
                style={{ '--stage-color': stage.color || '#38bdf8' }}
                onClick={() => {
                  setFilters(f => ({ ...f, stageKey: f.stageKey === stage.stage_key ? '' : stage.stage_key }));
                  setActiveTab('contacts');
                }}
              >
                <div className="cfv-stage-count">{stage.count}</div>
                <div className="cfv-stage-name">{stage.stage_name}</div>
                <div className="cfv-stage-meta">
                  {stage.with_app_access > 0 && (
                    <span className="cfv-stage-access"><FaUserCheck /> {stage.with_app_access}</span>
                  )}
                </div>
                <div className="cfv-stage-bar" style={{ width: funnelData.totals.contacts > 0 ? `${(stage.count / funnelData.totals.contacts) * 100}%` : '0%' }} />
              </button>
            ))}
          </div>
        </div>

        {/* Reconciliation status */}
        {reconcileStatus && (
          <div className="cfv-reconcile-section">
            <h3 className="cfv-section-title">CRM ↔ SQL Link Status</h3>
            <div className="cfv-reconcile-stats">
              <div className="cfv-reconcile-bar">
                <div className="cfv-reconcile-fill" style={{ width: `${reconcileStatus.linkPercentage}%` }} />
              </div>
              <div className="cfv-reconcile-text">
                <span><FaLink /> {reconcileStatus.linked} linked</span>
                <span><FaUnlink /> {reconcileStatus.unlinked} unlinked</span>
                <span>{reconcileStatus.linkPercentage}% coverage</span>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ── CONTACTS TAB ──────────────────────────────────────────
  const renderContactsTab = () => (
    <div className="cfv-contacts-section">
      {/* Filters bar */}
      <div className="cfv-filters">
        <div className="cfv-search-box">
          <FaSearch />
          <input
            type="text"
            placeholder="Search contacts..."
            value={filters.search}
            onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
            onKeyDown={(e) => e.key === 'Enter' && fetchContacts()}
          />
        </div>
        <select
          value={filters.stageKey}
          onChange={(e) => setFilters(f => ({ ...f, stageKey: e.target.value }))}
          className="cfv-select"
        >
          <option value="">All Stages</option>
          {stages.map(s => (
            <option key={s.stage_key} value={s.stage_key}>{s.stage_name}</option>
          ))}
        </select>
        <select
          value={filters.hasAppAccess}
          onChange={(e) => setFilters(f => ({ ...f, hasAppAccess: e.target.value }))}
          className="cfv-select"
        >
          <option value="">All Access</option>
          <option value="true">Has App Access</option>
          <option value="false">CRM Only</option>
        </select>
        <button className="cfv-btn cfv-btn-secondary" onClick={fetchContacts}>
          <FaFilter /> Apply
        </button>
      </div>

      {/* Results count */}
      <div className="cfv-results-count">
        {contactsTotal} contact{contactsTotal !== 1 ? 's' : ''} found
      </div>

      {/* Contacts list */}
      {contactsLoading ? (
        <div className="cfv-loading">Loading contacts...</div>
      ) : contacts.length === 0 ? (
        <div className="cfv-empty">No contacts match your filters</div>
      ) : (
        <div className="cfv-contacts-list">
          {contacts.map((c) => (
            <div key={c.id} className={`cfv-contact-row ${expandedContact === c.id ? 'expanded' : ''}`}>
              <button className="cfv-contact-header" onClick={() => toggleContact(c.id)}>
                <div className="cfv-contact-main">
                  <span className="cfv-contact-name">{c.first_name} {c.last_name}</span>
                  <span className="cfv-contact-email">{c.email}</span>
                </div>
                <div className="cfv-contact-badges">
                  {renderStageBadge(c.stage_key, c.stage_name, c.stage_color)}
                  {renderAccessBadge(c.app_user_id, c.app_role)}
                </div>
                <span className="cfv-contact-chevron">
                  {expandedContact === c.id ? <FaChevronDown /> : <FaChevronRight />}
                </span>
              </button>

              {expandedContact === c.id && contactDetail && (
                <div className="cfv-contact-detail">
                  <div className="cfv-detail-grid">
                    <div className="cfv-detail-section">
                      <h4>CRM Contact <span className="cfv-owner-badge">CRM Owned</span></h4>
                      <div className="cfv-detail-row"><span>Email</span><span>{contactDetail.contact.email}</span></div>
                      <div className="cfv-detail-row"><span>Phone</span><span>{contactDetail.contact.phone || '—'}</span></div>
                      <div className="cfv-detail-row"><span>Company</span><span>{contactDetail.contact.company_name || '—'}</span></div>
                      <div className="cfv-detail-row"><span>Source</span><span>{contactDetail.contact.source || '—'}</span></div>
                      <div className="cfv-detail-row"><span>Role Requested</span><span>{contactDetail.contact.role_requested || '—'}</span></div>
                      <div className="cfv-detail-row"><span>Created</span><span>{new Date(contactDetail.contact.created_at).toLocaleDateString()}</span></div>
                      {/* Stage changer */}
                      <div className="cfv-detail-row">
                        <span>Stage</span>
                        <select
                          value={contactDetail.contact.stage_key || ''}
                          onChange={(e) => handleStageChange(c.id, e.target.value)}
                          className="cfv-select cfv-select-sm"
                        >
                          {stages.map(s => (
                            <option key={s.stage_key} value={s.stage_key}>{s.stage_name}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="cfv-detail-section">
                      <h4>App Access <span className="cfv-owner-badge cfv-owner-sql">SQL Owned</span></h4>
                      {contactDetail.contact.app_user_id ? (
                        <>
                          <div className="cfv-detail-row"><span>User ID</span><span>{contactDetail.contact.app_user_id}</span></div>
                          <div className="cfv-detail-row"><span>Role</span><span>{contactDetail.contact.app_role}</span></div>
                          <div className="cfv-detail-row"><span>Account Type</span><span>{contactDetail.contact.app_account_type || '—'}</span></div>
                          <div className="cfv-detail-row"><span>User Code</span><span>{contactDetail.contact.app_user_code || '—'}</span></div>
                          <div className="cfv-detail-row"><span>Active</span><span>{contactDetail.contact.app_is_active ? 'Yes' : 'No'}</span></div>
                          <div className="cfv-detail-row"><span>Created</span><span>{new Date(contactDetail.contact.app_created_at).toLocaleDateString()}</span></div>
                        </>
                      ) : (
                        <div className="cfv-no-access">No CloudSafe app access created yet</div>
                      )}
                    </div>
                  </div>

                  {/* Tags */}
                  {contactDetail.tags && contactDetail.tags.length > 0 && (
                    <div className="cfv-tags-section">
                      <h4><FaTag /> Tags</h4>
                      <div className="cfv-tags-list">
                        {contactDetail.tags.map(tag => (
                          <span key={tag} className="cfv-tag">{tag}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Stage history */}
                  {contactDetail.stageHistory && contactDetail.stageHistory.length > 0 && (
                    <div className="cfv-history-section">
                      <h4><FaHistory /> Stage History</h4>
                      <div className="cfv-history-list">
                        {contactDetail.stageHistory.slice(0, 10).map((h) => (
                          <div key={h.id} className="cfv-history-item">
                            <span className="cfv-history-from">{h.from_stage_name || '—'}</span>
                            <span className="cfv-history-arrow">→</span>
                            <span className="cfv-history-to">{h.to_stage_name || '—'}</span>
                            <span className="cfv-history-meta">
                              {h.changed_by} · {new Date(h.created_at).toLocaleString()}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ── RECONCILE TAB ─────────────────────────────────────────
  const renderReconcileTab = () => (
    <div className="cfv-reconcile-full">
      <div className="cfv-reconcile-header">
        <h3>CRM ↔ SQL Reconciliation</h3>
        <p className="cfv-reconcile-desc">
          Link existing SQL users to CRM contacts. Users without a <code>crm_contact_id</code> will
          be matched by email. If no CRM contact exists, one will be created.
        </p>
      </div>

      {reconcileStatus && (
        <div className="cfv-stat-row">
          <div className="cfv-stat-card">
            <div className="cfv-stat-value">{reconcileStatus.total}</div>
            <div className="cfv-stat-label">Total Users</div>
          </div>
          <div className="cfv-stat-card cfv-stat-success">
            <div className="cfv-stat-value">{reconcileStatus.linked}</div>
            <div className="cfv-stat-label">Linked to CRM</div>
          </div>
          <div className="cfv-stat-card cfv-stat-warning">
            <div className="cfv-stat-value">{reconcileStatus.unlinked}</div>
            <div className="cfv-stat-label">Unlinked</div>
          </div>
        </div>
      )}

      <div className="cfv-reconcile-actions">
        <button className="cfv-btn cfv-btn-secondary" onClick={handleReconcilePreview}>
          <FaSearch /> Preview (Dry Run)
        </button>
        <button
          className="cfv-btn cfv-btn-primary"
          onClick={handleReconcileRun}
          disabled={reconcileRunning || (reconcileStatus && reconcileStatus.unlinked === 0)}
        >
          <FaSyncAlt className={reconcileRunning ? 'cfv-spin' : ''} />
          {reconcileRunning ? 'Running...' : 'Run Reconciliation'}
        </button>
      </div>

      {reconcilePreview && (
        <div className="cfv-reconcile-results">
          <h4>Results: {reconcilePreview.processed} processed</h4>
          <div className="cfv-reconcile-summary">
            <span><FaCheckCircle className="cfv-icon-success" /> {reconcilePreview.linked} linked</span>
            <span><FaCheckCircle className="cfv-icon-success" /> {reconcilePreview.created} created</span>
            <span><FaExclamationTriangle className="cfv-icon-warning" /> {reconcilePreview.skipped} skipped</span>
            <span><FaTimes className="cfv-icon-danger" /> {reconcilePreview.errors} errors</span>
          </div>
          {reconcilePreview.details && reconcilePreview.details.length > 0 && (
            <div className="cfv-reconcile-detail-list">
              {reconcilePreview.details.map((d, i) => (
                <div key={i} className={`cfv-reconcile-detail-item cfv-reconcile-${d.action.includes('error') ? 'error' : d.action.includes('skip') ? 'skip' : 'ok'}`}>
                  <span className="cfv-reconcile-email">{d.email}</span>
                  <span className="cfv-reconcile-action">{d.action}</span>
                  {d.reason && <span className="cfv-reconcile-reason">{d.reason}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="cs-dashboard cfv-page">
      <header className="cs-header">
        <div className="cs-header-left">
          <button className="cs-mobile-menu-btn" onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}>
            <FaBars />
          </button>
          <div className="cs-header-badge"><FaServer /><span>Cloud Staff Portal</span></div>
        </div>
        <div className="cs-header-right">
          <div className="cs-header-icons">
            <div className="cs-user-menu-wrapper">
              <button className="cs-icon-btn" onClick={() => setShowUserMenu(!showUserMenu)}>
                <FaUserCircle />
              </button>
              {showUserMenu && (
                <div className="cs-user-dropdown">
                  <div className="cs-dropdown-name">{userName}</div>
                  <button className="cs-dropdown-item" onClick={handleLogout}>
                    <FaSignOutAlt /> Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="cs-layout">
        <CloudStaffSidebar
          active="crm-funnel"
          mobileOpen={mobileSidebarOpen}
          onClose={() => setMobileSidebarOpen(false)}
        />
        <main className="cs-content">
          <div className="cfv-header">
            <div>
              <h1 className="cfv-title"><FaUsers /> CRM Pipeline</h1>
              <p className="cfv-subtitle">Contact lifecycle and onboarding funnel</p>
            </div>
            <button className="cfv-btn cfv-btn-secondary" onClick={() => { fetchFunnelData(); fetchReconcileStatus(); }}>
              <FaSyncAlt /> Refresh
            </button>
          </div>

          {/* Tabs */}
          <div className="cfv-tabs">
            <button className={`cfv-tab ${activeTab === 'funnel' ? 'active' : ''}`} onClick={() => setActiveTab('funnel')}>
              Pipeline Overview
            </button>
            <button className={`cfv-tab ${activeTab === 'contacts' ? 'active' : ''}`} onClick={() => setActiveTab('contacts')}>
              Contacts ({contactsTotal || '...'})
            </button>
            <button className={`cfv-tab ${activeTab === 'reconcile' ? 'active' : ''}`} onClick={() => setActiveTab('reconcile')}>
              Reconciliation
              {reconcileStatus && reconcileStatus.unlinked > 0 && (
                <span className="cfv-tab-badge">{reconcileStatus.unlinked}</span>
              )}
            </button>
          </div>

          {/* Tab content */}
          {activeTab === 'funnel' && renderFunnelTab()}
          {activeTab === 'contacts' && renderContactsTab()}
          {activeTab === 'reconcile' && renderReconcileTab()}
        </main>
      </div>
    </div>
  );
};

export default CrmFunnelView;
